import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import BatteryAlertOutlinedIcon from '@mui/icons-material/BatteryAlertOutlined';
import RadioOutlinedIcon from '@mui/icons-material/RadioOutlined';
import HearingIcon from '@mui/icons-material/Hearing';
import SettingsInputAntennaIcon from '@mui/icons-material/SettingsInputAntenna';
import SensorsOffOutlinedIcon from '@mui/icons-material/SensorsOffOutlined';
import SyncProblemOutlinedIcon from '@mui/icons-material/SyncProblemOutlined';

import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import Tooltip from '@mui/material/Tooltip';

import UIDateFormater, {
  DateType,
} from '../../../containers/UIhelper/UIDateFormater';

const statusIcons = {
  low_noise: <HearingIcon />,
  white_noise: <HearingIcon />,
  radio_sync: <SettingsInputAntennaIcon />,
  no_radio: <RadioOutlinedIcon />,
  low_battery: <BatteryAlertOutlinedIcon />,
  status: <SensorsOffOutlinedIcon />,
};

const getStatusIcons = (row) => {
  const icons = [];

  // Audio
  if (row.low_noise || row.white_noise) {
    const tooltipContent = [];
    if (row.low_noise) {
      tooltipContent.push('Low noise');
    }
    if (row.white_noise) {
      tooltipContent.push('White noise');
    }
    icons.push({
      key: 'audio',
      icon: statusIcons.low_noise,
      tooltip: tooltipContent.join(' / '),
    });
  }

  // FM Reception
  if (row.radio_sync)
    icons.push({
      key: 'fm-sync',
      icon: statusIcons.radio_sync,
      tooltip: 'FM Sync problem',
    });

  if (row.radio_white_noise)
    icons.push({
      key: 'fm-sync',
      icon: statusIcons.radio_sync,
      tooltip: 'Radio white noise',
    });

  // No FM
  if (row.no_radio /*|| row.radio_white_noise*/) {
    const tooltipContent = [];
    if (row.no_radio) {
      tooltipContent.push('No radio');
    }
    // if (row.radio_white_noise) {
    //   tooltipContent.push('Radio white noise');
    // }
    icons.push({
      key: 'no-fm',
      icon: statusIcons.no_radio,
      tooltip: tooltipContent.join(' / '),
    });
  }

  // Battery
  if (row.low_battery)
    icons.push({
      key: 'low-battery',
      icon: statusIcons.low_battery,
      tooltip: 'Low Battery',
    });

  // Inactive (status = 0 means inactive)
  if (row.inactive)
    icons.push({
      key: 'inactive',
      icon: statusIcons.status,
      tooltip: 'Inactive sensor',
    });

  return icons;
};

const getFirstValue = (sensor, fields) => {
  const field = fields.find(
    (key) => sensor?.[key] !== undefined && sensor?.[key] !== null
  );
  if (sensor[field] === 'NULL' || sensor[field] === '') return '--';
  return field ? sensor[field] : '--';
};

const getSensorId = (sensor) =>
  getFirstValue(sensor, [
    'SensorID',
    'Sensor ID',
    'sensorId',
    'sensor_id',
    'id',
    'ID',
  ]);

const getSensorSelectionIds = (sensor) =>
  [
    sensor?.id,
    sensor?.ID,
    sensor?.SensorID,
    sensor?.['Sensor ID'],
    sensor?.sensorId,
    sensor?.sensor_id,
  ].filter((value) => value !== undefined && value !== null);

const formatYesNo = (value, t) => {
  if (value === '--' || value === '') return '--';
  if (typeof value === 'boolean') return value ? t('yes') : t('no');
  if (typeof value === 'number') return value === 0 ? t('no') : t('yes');

  const normalizedValue = String(value).trim().toLowerCase();
  if (['true', 'yes', '1'].includes(normalizedValue)) return t('yes');
  if (['false', 'no', '0'].includes(normalizedValue)) return t('no');
  return value;
};

const formatBatteryValue = (value) => {
  if (value === '--' || value === '') return '--';

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue.toFixed(2) : value;
};

const normalizeTimestamp = (value) => {
  if (value === '--' || value === '' || value === null || value === undefined) {
    return null;
  }

  const numericValue =
    typeof value === 'number' || /^\d+$/.test(String(value).trim())
      ? Number(value)
      : Date.parse(value);

  if (!Number.isFinite(numericValue)) return null;
  return numericValue < 1000000000000 ? numericValue * 1000 : numericValue;
};

const formatSampleTime = (value, timeZone, projectTimeZone) => {
  const timestamp = normalizeTimestamp(value);
  if (timestamp === null) return value === '--' || value === '' ? '--' : value;

  return UIDateFormater(
    timestamp,
    DateType.DATE_AND_TIME,
    timeZone,
    projectTimeZone
  );
};

const getMaintenanceAction = (sensor, t) => {
  const batteryOrRadio = sensor.low_battery || sensor.no_radio;
  const audio = sensor.white_noise || sensor.low_noise;

  if (batteryOrRadio && audio) {
    return `${t('sensor_replacement')} + ${t('tail_replacement')}`;
  }
  if (batteryOrRadio) return t('sensor_replacement');
  if (audio) return t('tail_replacement');
  return t('to_check');
};

const getComparableValue = (value, sortType) => {
  if (value === '--' || value === '' || value === null || value === undefined) {
    return null;
  }

  if (sortType === 'number') {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : null;
  }

  if (sortType === 'date') {
    const timestamp = normalizeTimestamp(value);
    return timestamp === null ? String(value).toLowerCase() : timestamp;
  }

  return String(value).toLowerCase();
};

const sortReportSensors = (sensors, columns, sortConfig) => {
  if (!sortConfig?.key) return sensors;

  const column = columns.find(({ key }) => key === sortConfig.key);
  if (!column) return sensors;

  return sensors
    .map((sensor, index) => ({ sensor, index }))
    .sort((left, right) => {
      const leftValue = getComparableValue(
        column.sortValue
          ? column.sortValue(left.sensor)
          : column.value(left.sensor),
        column.sortType
      );
      const rightValue = getComparableValue(
        column.sortValue
          ? column.sortValue(right.sensor)
          : column.value(right.sensor),
        column.sortType
      );

      if (leftValue === null && rightValue === null) {
        return left.index - right.index;
      }
      if (leftValue === null) return 1;
      if (rightValue === null) return -1;

      const comparison =
        typeof leftValue === 'number' && typeof rightValue === 'number'
          ? leftValue - rightValue
          : String(leftValue).localeCompare(String(rightValue), undefined, {
            numeric: true,
            sensitivity: 'base',
          });

      if (comparison === 0) return left.index - right.index;
      return sortConfig.direction === 'desc' ? -comparison : comparison;
    })
    .map(({ sensor }) => sensor);
};

const getMaintenanceColumns = (t) => [
  {
    label: '#',
    // minWidth: 80,
    value: (_sensor, rowIndex) => rowIndex + 1,
  },
  {
    label: 'Issues',
    // minWidth: 80,
    value: (sensor, rowIndex) => {
      const icons = getStatusIcons(sensor);

      if (icons.length === 0) {
        return <span style={{ color: '#9e9e9e' }}>—</span>;
      }

      return (
        <Box
          sx={{
            display: 'flex',
            gap: 0.5,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          {icons.map((icon, index) => (
            <Box
              key={`${rowIndex}-${index}`}
              title={icon?.tooltip}
              sx={{ display: 'flex' }}
            >
              {icon?.icon}
            </Box>
          ))}
        </Box>
      );
    },
  },
  {
    key: 'action',
    label: 'Action',
    // minWidth: 80,
    value: (sensor) => getMaintenanceAction(sensor, t),
  },
  {
    key: 'sensorId',
    label: 'Sensor ID',
    // minWidth: 80,
    value: getSensorId,
  },
  {
    key: 'deviceId',
    label: 'Device ID',
    // minWidth: 125,
    value: (sensor) =>
      getFirstValue(sensor, ['DeviceID', 'Device ID', 'deviceId', 'device_id']),
  },
  {
    key: 'status',
    label: 'Status',
    // minWidth: 80,
    value: (sensor) => (sensor.inactive ? t('inactive') : t('active')),
  },
  {
    key: 'batteryValue',
    label: 'Battery Value',
    // minWidth: 80,
    sortType: 'number',
    value: (sensor) =>
      formatBatteryValue(
        getFirstValue(sensor, [
          'BatteryValue',
          'Battery Value',
          'batteryValue',
          'battery_value',
          'Battery',
          'battery',
        ])
      ),
  },
  {
    key: 'whiteNoise',
    label: 'White Noise',
    // minWidth: 80,
    value: (sensor) =>
      formatYesNo(
        getFirstValue(sensor, [
          'WhiteNoise',
          'White Noise',
          'whiteNoise',
          'white_noise',
        ]),
        t
      ),
  },
  {
    key: 'lowNoise',
    label: 'Low Noise',
    // minWidth: 80,
    value: (sensor) =>
      formatYesNo(
        getFirstValue(sensor, [
          'LowNoise',
          'Low Noise',
          'lowNoise',
          'low_noise',
        ]),
        t
      ),
  },
  {
    key: 'noRadio',
    label: 'No Radio',
    // minWidth: 80,
    value: (sensor) =>
      formatYesNo(
        getFirstValue(sensor, ['NoRadio', 'No Radio', 'noRadio', 'no_radio']),
        t
      ),
  },
  {
    key: 'lowBattery',
    label: 'Battery Issues',
    // minWidth: 80,
    value: (sensor) =>
      formatYesNo(
        getFirstValue(sensor, [
          'LowBattery',
          'Low Battery',
          'lowBattery',
          'low_battery',
        ]),
        t
      ),
  },
  {
    key: 'radioWhiteNoise',
    label: 'Radio White Noise',
    // minWidth: 80,
    value: (sensor) =>
      formatYesNo(
        getFirstValue(sensor, [
          'RadioWhiteNoise',
          'Radio White Noise',
          'radioWhiteNoise',
          'radio_white_noise',
        ]),
        t
      ),
  },
  {
    key: 'radioSync',
    label: 'Radio Sync Issues',
    // minWidth: 80,
    value: (sensor) => formatYesNo(getFirstValue(sensor, ['radio_sync']), t),
  },
  {
    key: 'samplesLastWeek',
    label: 'Samples Last Week',
    // minWidth: 80,
    sortType: 'number',
    value: (sensor) =>
      getFirstValue(sensor, [
        'SamplesLastWeek',
        'Samples Last Week',
        'samplesLastWeek',
        'samples_last_week',
      ]),
  },

  {
    key: 'sampleTime',
    label: 'Sample Time',
    minWidth: 110,
    sortType: 'date',
    value: (sensor) =>
      getFirstValue(sensor, [
        'SampleTime',
        'Sample Time',
        'sampleTime',
        'sample_time',
        'LastSampleTime',
        'last_sample_time',
      ]),
  },
  {
    key: 'address',
    label: 'Address',
    minWidth: 220,
    align: 'left',
    value: (sensor) => getFirstValue(sensor, ['address', 'Address']),
  },
];

const ReportSensorsTable = ({
  reportSensors,
  onSensorClick,
  emptyText,
  height,
  selectedSensorId,
  sortConfig,
  onSortChange,
  timeZone,
  projectTimeZone,
  slowVerticalScroll,
  className,
  useNativeTable,
  t,
}) => {
  const scrollContainerRef = useRef(null);
  const rowRefs = useRef({});
  const baseColumns = getMaintenanceColumns(t);
  const columns = baseColumns.map((column) =>
    column.key === 'sampleTime'
      ? {
        ...column,
        sortValue: column.value,
        value: (sensor) =>
          formatSampleTime(column.value(sensor), timeZone, projectTimeZone),
      }
      : column
  );
  const sortedSensors = sortReportSensors(reportSensors, columns, sortConfig);
  const selectedRowKey = useMemo(() => {
    const selectedSensor = sortedSensors.find((sensor) =>
      getSensorSelectionIds(sensor).some(
        (id) => String(id) === String(selectedSensorId)
      )
    );

    return selectedSensor ? String(getSensorId(selectedSensor)) : null;
  }, [selectedSensorId, sortedSensors]);

  useEffect(() => {
    if (!selectedRowKey) return;

    const selectedRowElement = rowRefs.current[selectedRowKey];
    if (!selectedRowElement) return;

    selectedRowElement.scrollIntoView({
      block: 'center',
      inline: 'nearest',
      behavior: 'smooth',
    });
  }, [selectedRowKey]);

  const renderNativeSortableHeader = (column) => {
    const isSorted = sortConfig.key === column.key;

    return (
      <th
        key={column.key}
        className={`report-sensors-native-header ${isSorted ? 'is-sorted' : ''
          }`}
        style={{
          minWidth: column.minWidth,
          textAlign: column.align,
        }}
        onClick={!column.key ? null : () => onSortChange(column.key)}
      >
        <span className='report-sensors-native-header-content'>
          {column.label}
          <span
            className={`report-sensors-native-sort-icon ${isSorted ? 'active' : ''
              }`}
          >
            {isSorted && sortConfig.direction === 'desc' ? (
              <ArrowDownwardIcon fontSize='small' />
            ) : (
              <ArrowUpwardIcon fontSize='small' />
            )}
          </span>
        </span>
      </th>
    );
  };

  const handleWheel = (event) => {
    if (
      !slowVerticalScroll ||
      Math.abs(event.deltaY) <= Math.abs(event.deltaX)
    ) {
      return;
    }

    const scrollContainer = event.currentTarget;
    if (scrollContainer.scrollHeight <= scrollContainer.clientHeight) return;

    const deltaMultiplier =
      event.deltaMode === 1
        ? 16
        : event.deltaMode === 2
          ? scrollContainer.clientHeight
          : 1;

    event.preventDefault();
    event.stopPropagation();
    scrollContainer.scrollTop += event.deltaY * deltaMultiplier * 0.35;
  };

  return (
    <Box
      className={`report-sensors-table ${className}`.trim()}
      sx={{
        minHeight: 0,
        flex: '1 1 0',
        ...(height
          ? {
            height,
            maxHeight: height,
          }
          : {}),
      }}
    >
      {reportSensors.length ? (
        useNativeTable ? (
          <Box
            className='report-sensors-table-scroll report-sensors-native-container'
            onWheel={handleWheel}
            ref={scrollContainerRef}
          >
            <table
              className='report-sensors-native-table'
              aria-label='maintenance report sensors'
            >
              <thead>
                <tr>{columns.map(renderNativeSortableHeader)}</tr>
              </thead>
              <tbody>
                {sortedSensors.map((sensor, rowIndex) => {
                  const sensorId = getSensorId(sensor);
                  const isSelected = getSensorSelectionIds(sensor).some(
                    (id) => String(id) === String(selectedSensorId)
                  );

                  return (
                    <tr
                      key={`${sensorId}-${rowIndex}`}
                      ref={(node) => {
                        if (!sensorId) return;
                        if (node) {
                          rowRefs.current[String(sensorId)] = node;
                        } else {
                          delete rowRefs.current[String(sensorId)];
                        }
                      }}
                      className={`report-sensor-row ${isSelected ? 'is-selected' : ''
                        }`}
                      onClick={() => onSensorClick(sensor)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onSensorClick(sensor);
                        }
                      }}
                      tabIndex={0}
                    >
                      {columns.map((column) => (
                        <td
                          key={column.key}
                          className={`report-sensor-cell report-sensor-cell-${column.key}`}
                          style={{
                            minWidth: column.minWidth,
                            textAlign: column.align,
                          }}
                        >
                          {column.value(sensor, rowIndex)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>
        ) : (
          <TableContainer
            className='report-sensors-table-scroll'
            onWheel={handleWheel}
            ref={scrollContainerRef}
          >
            <Table
              stickyHeader
              size='small'
              aria-label='maintenance report sensors'
            >
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.key}
                      sx={{ minWidth: column.minWidth, textAlign: column.align }}
                    >
                      <TableSortLabel
                        disabled={!column.key}
                        active={sortConfig.key === column.key}
                        direction={
                          sortConfig.key === column.key
                            ? sortConfig.direction
                            : 'asc'
                        }
                        onClick={
                          !column.key ? null : () => onSortChange(column.key)
                        }
                      >
                        {column.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedSensors.map((sensor, rowIndex) => {
                  const sensorId = getSensorId(sensor);
                  const isSelected = getSensorSelectionIds(sensor).some(
                    (id) => String(id) === String(selectedSensorId)
                  );

                  return (
                    <TableRow
                      hover
                      key={`${sensorId}-${rowIndex}`}
                      ref={(node) => {
                        if (!sensorId) return;
                        if (node) {
                          rowRefs.current[String(sensorId)] = node;
                        } else {
                          delete rowRefs.current[String(sensorId)];
                        }
                      }}
                      className={`report-sensor-row ${isSelected ? 'is-selected' : ''
                        }`}
                      onClick={() => onSensorClick(sensor)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onSensorClick(sensor);
                        }
                      }}
                      tabIndex={0}
                    >
                      {columns.map((column) => (
                        <TableCell
                          key={column.key}
                          className={`report-sensor-cell report-sensor-cell-${column.key}`}
                          sx={{ textAlign: column.align }}
                        >
                          {column.value(sensor, rowIndex)}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )
      ) : (
        <Box className='report-sensors-empty'>
          <Typography>{emptyText}</Typography>
        </Box>
      )}
    </Box>
  );
};

ReportSensorsTable.propTypes = {
  reportSensors: PropTypes.arrayOf(PropTypes.object),
  onSensorClick: PropTypes.func.isRequired,
  emptyText: PropTypes.string,
  height: PropTypes.number,
  className: PropTypes.string,
  useNativeTable: PropTypes.bool,
  selectedSensorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sortConfig: PropTypes.shape({
    key: PropTypes.string,
    direction: PropTypes.oneOf(['asc', 'desc']).isRequired,
  }).isRequired,
  onSortChange: PropTypes.func.isRequired,
  timeZone: PropTypes.string,
  projectTimeZone: PropTypes.string,
  slowVerticalScroll: PropTypes.bool,
  t: PropTypes.func,
};

ReportSensorsTable.defaultProps = {
  reportSensors: [],
  emptyText: 'No sensors found for the Maintenance report.',
  height: null,
  className: '',
  useNativeTable: false,
  selectedSensorId: '',
  timeZone: 'gmt',
  projectTimeZone: '',
  slowVerticalScroll: false,
  t: (key) => key,
};

export default ReportSensorsTable;
